import { emptyLoanPaymentDraft, type LoanPaymentDraft } from "@/components/hr/LoanPaymentMethodFields";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { EmployeeSelect } from "@/components/hr/EmployeeSelect";
import { SalaryPeriodMonthPicker } from "@/components/hr/SalaryPeriodMonthPicker";
import {
  applyPaymentMethodChange,
  loanDraftToPaymentDraft,
  paymentDraftToLoanDraft,
  PaymentMethodDetailFields,
  PaymentMethodSelect,
  paymentMethodUsesBank,
  type PaymentMethodValue,
} from "@/components/sales/PaymentMethodFields";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { formatAmountCommas } from "@/lib/formatPkrAmount";
import { getEmployeeById, listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import { validateLoanPayment } from "@/lib/hr/loansStore";
import {
  createAdvanceSalaryBatch,
  fetchAdvanceSalaryFormMeta,
  periodKey,
  periodLabel,
  salaryAdvancesApiErrorMessage,
  type AdvanceSalaryFormMeta,
} from "@/lib/hr/salaryAdvancesStore";
import { fetchBanks, type BankOption } from "@/lib/sales/banksStore";
import { cn } from "@/lib/utils";
import { CheckCircle2, HandCoins, Info, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const MONTHS = [
  { v: 1, label: "January" },
  { v: 2, label: "February" },
  { v: 3, label: "March" },
  { v: 4, label: "April" },
  { v: 5, label: "May" },
  { v: 6, label: "June" },
  { v: 7, label: "July" },
  { v: 8, label: "August" },
  { v: 9, label: "September" },
  { v: 10, label: "October" },
  { v: 11, label: "November" },
  { v: 12, label: "December" },
] as const;

function formatMoney(value: string): string {
  const raw = value.replace(/[^\d.]/g, "");
  if (!raw) return value;
  const [whole, ...rest] = raw.split(".");
  const formatted = formatAmountCommas(whole.replace(/\D/g, ""));
  return rest.length ? `${formatted}.${rest.join("")}` : formatted;
}

function safeNumber(v: string): number | null {
  const n = Number(v.replace(/,/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function roundMoney(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function buildPeriodOptions(meta: AdvanceSalaryFormMeta | null): Array<{
  year: number;
  month: number;
  key: string;
  label: string;
}> {
  const blocked = new Set((meta?.blockedPeriods ?? []).map((p) => periodKey(p.year, p.month)));
  const now = new Date();
  const options: Array<{ year: number; month: number; key: string; label: string }> = [];
  const start = new Date(now.getFullYear(), 0, 1);
  for (let offset = 0; offset < 24; offset += 1) {
    const d = new Date(start.getFullYear(), start.getMonth() + offset, 1);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const key = periodKey(year, month);
    // Only hide periods where payroll is already finalized/paid. Multiple advances per month are allowed.
    if (blocked.has(key)) continue;
    options.push({ year, month, key, label: periodLabel(year, month, MONTHS) });
  }
  return options;
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export type SalaryAdvanceCreateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, employee is locked (employee profile). Omit on /payroll/salary-advances. */
  employeeId?: string;
  onSaved?: () => void;
};

export function SalaryAdvanceCreateDialog({ open, onOpenChange, employeeId, onSaved }: SalaryAdvanceCreateDialogProps) {
  const lockedEmployeeId = employeeId || "";
  const allowEmployeeSelect = !lockedEmployeeId;

  const [selectedEmployeeId, setSelectedEmployeeId] = useState(lockedEmployeeId);
  const [employee, setEmployee] = useState<EmployeeRecord | undefined>(() =>
    lockedEmployeeId ? getEmployeeById(lockedEmployeeId) : undefined,
  );
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [periodYear, setPeriodYear] = useState(0);
  const [periodMonth, setPeriodMonth] = useState(0);
  const [advanceDate, setAdvanceDate] = useState(todayIso);
  const [reason, setReason] = useState("");
  const [paymentDraft, setPaymentDraft] = useState<LoanPaymentDraft>(emptyLoanPaymentDraft());
  const [formMeta, setFormMeta] = useState<AdvanceSalaryFormMeta | null>(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [banks, setBanks] = useState<BankOption[]>([]);
  const [empTick, setEmpTick] = useState(0);
  const [periodPickerOpen, setPeriodPickerOpen] = useState(false);

  const employees = useMemo(() => listEmployees(), [empTick]);
  const paymentDraftFull = useMemo(() => loanDraftToPaymentDraft(paymentDraft), [paymentDraft]);
  const amountNum = safeNumber(advanceAmount);
  const periodOptions = useMemo(() => buildPeriodOptions(formMeta), [formMeta]);

  const periodTaken = useMemo(() => {
    if (!formMeta || !periodYear || !periodMonth) return 0;
    const row = formMeta.periodAdvances.find((p) => p.year === periodYear && p.month === periodMonth);
    return row?.total ?? 0;
  }, [formMeta, periodYear, periodMonth]);

  const periodTakenCount = useMemo(() => {
    if (!formMeta || !periodYear || !periodMonth) return 0;
    const row = formMeta.periodAdvances.find((p) => p.year === periodYear && p.month === periodMonth);
    return row?.count ?? 0;
  }, [formMeta, periodYear, periodMonth]);

  const grossCap = formMeta?.grossSalary ?? 0;
  const availableCap = Math.max(0, roundMoney(grossCap - periodTaken));
  const thisAdvance = amountNum ?? 0;
  const remainingAfter = Math.max(0, roundMoney(availableCap - thisAdvance));
  const exceedsCap = thisAdvance > availableCap + 0.005;
  const periodSelected = Boolean(periodYear && periodMonth);

  const employeeName = employee
    ? employee.fullName || `${employee.firstName} ${employee.lastName}`.trim() || "Employee"
    : "Employee";
  const employeeInitial = employeeName.trim().charAt(0).toUpperCase() || "E";

  useEffect(() => {
    if (!open) return;
    setAdvanceAmount("");
    setPeriodYear(0);
    setPeriodMonth(0);
    setAdvanceDate(todayIso());
    setReason("");
    setPaymentDraft(emptyLoanPaymentDraft());
    setFormMeta(null);
    setPeriodPickerOpen(false);
    setSelectedEmployeeId(lockedEmployeeId);
    setEmployee(lockedEmployeeId ? getEmployeeById(lockedEmployeeId) : undefined);
  }, [open, lockedEmployeeId]);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    void Promise.all([fetchBanks(), syncEmployeesFromApi()])
      .then(([rows]) => {
        if (mounted) {
          setBanks(rows);
          setEmpTick((t) => t + 1);
        }
      })
      .catch(() => {
        if (mounted) setBanks([]);
      });
    return () => {
      mounted = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !selectedEmployeeId) {
      setFormMeta(null);
      return;
    }
    let cancelled = false;
    setMetaLoading(true);
    void fetchAdvanceSalaryFormMeta(selectedEmployeeId)
      .then((meta) => {
        if (!cancelled) setFormMeta(meta);
      })
      .catch((e) => {
        if (!cancelled) {
          setFormMeta(null);
          toast.error(salaryAdvancesApiErrorMessage(e));
        }
      })
      .finally(() => {
        if (!cancelled) setMetaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectedEmployeeId]);

  const close = () => onOpenChange(false);

  const save = async () => {
    if (saving) return;
    if (!selectedEmployeeId) return toast.error("Select an employee.");
    if (!formMeta) return toast.error("Employee salary details are still loading.");
    if (!amountNum || amountNum <= 0) return toast.error("Advance amount must be greater than zero.");
    if (!periodYear || !periodMonth) return toast.error("Select a salary period.");
    if (!advanceDate.trim()) return toast.error("Select an advance date.");
    if (availableCap <= 0.005) {
      return toast.error(
        `No remaining gross salary for ${periodLabel(periodYear, periodMonth, MONTHS)}. Already taken: ${formatCurrency(periodTaken)}.`,
      );
    }
    if (exceedsCap) {
      return toast.error(
        `Advance cannot exceed remaining gross for this month (${formatCurrency(availableCap)}). Already taken: ${formatCurrency(periodTaken)}.`,
      );
    }
    const paymentError = validateLoanPayment({
      paymentMethod: paymentDraft.paymentMethod as "" | "cash" | "online" | "cheque" | "pay_order" | "cash_deposit",
      bankId: paymentDraft.bankId,
      referenceNo: paymentDraft.referenceNo,
      chequeNo: paymentDraft.chequeNo,
    });
    if (paymentError) return toast.error(paymentError);

    setSaving(true);
    try {
      const result = await createAdvanceSalaryBatch({
        employeeId: selectedEmployeeId,
        lines: [
          {
            advanceAmount: amountNum,
            advanceDate: advanceDate.trim(),
            periodYear,
            periodMonth,
            reason: reason.trim() || null,
          },
        ],
        paymentMethod: paymentDraft.paymentMethod,
        bankId: paymentDraft.bankId,
        referenceNo: paymentDraft.referenceNo,
        chequeNo: paymentDraft.chequeNo,
        chequeDate: paymentDraft.chequeDate,
      });
      toast.success(result.message);
      close();
      onSaved?.();
    } catch (e) {
      toast.error(salaryAdvancesApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
        <DialogHeader className="pb-3 border-b border-border/50">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-600">
              <HandCoins className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-foreground">Record Salary Advance</DialogTitle>
              <p className="text-xs text-muted-foreground">Issue one advance against an upcoming payroll month.</p>
            </div>
          </div>
        </DialogHeader>

        {allowEmployeeSelect ? (
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-foreground">
              Employee <span className="text-destructive">*</span>
            </Label>
            <EmployeeSelect
              value={selectedEmployeeId}
              onChange={(id) => {
                setSelectedEmployeeId(id);
                setEmployee(employees.find((e) => e.id === id) ?? getEmployeeById(id));
                setAdvanceAmount("");
                setPeriodYear(0);
                setPeriodMonth(0);
                setReason("");
              }}
              employees={employees}
              triggerClassName="h-10"
            />
          </div>
        ) : null}

        {employee ? (
          <div className="flex items-center justify-between bg-muted/30 border border-border/50 rounded-xl p-3 text-xs">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="h-8 w-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center font-bold text-primary text-xs overflow-hidden shrink-0">
                {employee.photoDataUrl ? <img src={employee.photoDataUrl} alt="" className="h-full w-full object-cover" /> : employeeInitial}
              </div>
              <div className="min-w-0">
                <p className="font-bold text-foreground truncate">{employeeName}</p>
                <p className="text-[11px] text-muted-foreground truncate">
                  {employee.designationName || "Staff"} · {employee.employeeCode || "—"}
                </p>
              </div>
            </div>
            <div className="text-right shrink-0 pl-3">
              <p className="text-[10px] uppercase font-semibold text-muted-foreground">Gross monthly salary</p>
              <p className="font-bold text-primary">
                {metaLoading ? "…" : formatCurrency(formMeta?.grossSalary ?? 0)}
              </p>
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-foreground">
                Salary Period <span className="text-destructive">*</span>
              </Label>
              <SalaryPeriodMonthPicker
                open={periodPickerOpen}
                onOpenChange={setPeriodPickerOpen}
                selectedYear={periodYear || null}
                selectedMonth={periodMonth || null}
                onSelect={(year, month) => {
                  setPeriodYear(year);
                  setPeriodMonth(month);
                }}
                availablePeriods={periodOptions}
                disabled={!selectedEmployeeId || metaLoading || !formMeta}
                description="This advance will be recovered in the selected payroll month."
                emptyMessage="No available salary periods for this employee."
                showSelectedFooter={false}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold text-foreground flex justify-between items-center">
                <span>
                  Advance Amount <span className="text-destructive">*</span>
                </span>
                {amountNum ? <span className="text-xs text-emerald-600 font-bold">{formatCurrency(amountNum)}</span> : null}
              </Label>
              <div className="relative">
                <Input
                  className="glass-input h-10 pr-12 font-semibold"
                  value={advanceAmount}
                  onChange={(e) => setAdvanceAmount(formatMoney(e.target.value))}
                  placeholder="e.g. 20,000"
                  inputMode="decimal"
                  disabled={!selectedEmployeeId}
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
                  PKR
                </span>
              </div>
              {formMeta && availableCap > 0 ? (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  <span className="text-[10px] text-muted-foreground self-center mr-1">Quick:</span>
                  {[0.25, 0.5, 0.75, 1].map((ratio) => {
                    const base = periodSelected ? availableCap : grossCap;
                    const calcAmt = Math.max(0, Math.round(base * ratio));
                    if (calcAmt <= 0) return null;
                    return (
                      <button
                        key={ratio}
                        type="button"
                        onClick={() => setAdvanceAmount(formatAmountCommas(String(calcAmt)))}
                        className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-border/60 bg-background hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-colors"
                      >
                        {Math.round(ratio * 100)}%
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold text-foreground">
                Date <span className="text-destructive">*</span>
              </Label>
              <DatePartsPicker
                value={advanceDate}
                onChange={setAdvanceDate}
                fromYear={1980}
                toYear={new Date().getFullYear() + 1}
                placeholder="Select advance date"
                disabled={!selectedEmployeeId}
              />
            </div>

            <PaymentMethodSelect
              value={(paymentDraft.paymentMethod || "") as PaymentMethodValue}
              onChange={(method) => {
                const next = applyPaymentMethodChange(paymentDraftFull, method);
                setPaymentDraft(paymentDraftToLoanDraft(next));
              }}
              allowNone={false}
              required
              placeholder="Select payment method"
              labelClassName="text-xs font-semibold text-foreground"
              triggerClassName="glass-input h-10"
              disabled={!selectedEmployeeId}
            />
          </div>

          <div
            className={cn(
              "flex flex-col rounded-xl border p-4",
              exceedsCap ? "border-rose-300/70 bg-rose-50/60 dark:bg-rose-950/20" : "border-primary/20 bg-primary/5",
            )}
          >
            <div className="flex items-center gap-2 pb-3 border-b border-border/40">
              <HandCoins className={cn("w-4 h-4", exceedsCap ? "text-rose-600" : "text-primary")} />
              <h4 className="font-bold text-xs uppercase tracking-wider text-foreground">Advance Preview</h4>
            </div>

            <div className="space-y-2.5 pt-3 text-xs flex-1">
              <div className="flex justify-between items-center gap-3">
                <span className="text-muted-foreground">Gross salary cap</span>
                <span className="font-bold tabular-nums text-foreground text-sm">
                  {formMeta ? formatCurrency(grossCap) : metaLoading ? "…" : "—"}
                </span>
              </div>

              <div className="flex justify-between items-center gap-3">
                <span className="text-muted-foreground">
                  Already taken
                  {periodSelected && periodTakenCount > 0 ? (
                    <span className="text-[10px] text-muted-foreground/80"> ({periodTakenCount})</span>
                  ) : null}
                </span>
                <span className="font-semibold tabular-nums text-amber-700 dark:text-amber-400">
                  {periodSelected ? formatCurrency(periodTaken) : "—"}
                </span>
              </div>

              <div className="flex justify-between items-center gap-3">
                <span className="text-muted-foreground">This advance</span>
                <span className={cn("font-bold tabular-nums text-sm", exceedsCap ? "text-rose-600" : "text-foreground")}>
                  {amountNum ? formatCurrency(thisAdvance) : "—"}
                </span>
              </div>

              <div className="flex justify-between items-center gap-3 border-t border-border/40 pt-2.5">
                <span className="text-muted-foreground">Recovered in</span>
                <span className="font-extrabold text-primary text-sm">
                  {periodSelected ? periodLabel(periodYear, periodMonth, MONTHS) : "—"}
                </span>
              </div>

              <div className="flex justify-between items-center gap-3 rounded-lg bg-background/70 px-2.5 py-2 border border-border/40">
                <span className="font-semibold text-foreground">Remaining of cap</span>
                <span
                  className={cn(
                    "font-extrabold tabular-nums text-sm",
                    exceedsCap
                      ? "text-rose-600"
                      : remainingAfter > 0
                        ? "text-emerald-700 dark:text-emerald-400"
                        : "text-foreground",
                  )}
                >
                  {periodSelected && formMeta ? formatCurrency(remainingAfter) : "—"}
                </span>
              </div>

              {periodSelected && availableCap <= 0.005 ? (
                <p className="rounded-md bg-rose-500/10 px-2.5 py-2 text-[11px] font-medium text-rose-700 dark:text-rose-300">
                  Gross salary for this month is fully used by existing advances. Another advance is not allowed.
                </p>
              ) : null}

              {exceedsCap ? (
                <p className="rounded-md bg-rose-500/10 px-2.5 py-2 text-[11px] font-medium text-rose-700 dark:text-rose-300">
                  This advance exceeds remaining gross ({formatCurrency(availableCap)}) after {formatCurrency(periodTaken)} already taken.
                </p>
              ) : null}

              {!periodSelected && formMeta ? (
                <p className="text-[11px] text-muted-foreground">
                  Select a salary period to see advances already taken and remaining gross for that month.
                </p>
              ) : null}
            </div>

            <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground pt-3 mt-3 border-t border-border/40">
              <Info className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
              <span>Total advances in one month cannot exceed gross salary. Recovered when that month’s payroll is finalized.</span>
            </div>
          </div>
        </div>

        {paymentDraft.paymentMethod && paymentMethodUsesBank(paymentDraft.paymentMethod as PaymentMethodValue) ? (
          <PaymentMethodDetailFields
            method={paymentDraftFull.paymentMethod}
            bankId={paymentDraftFull.bankId}
            transactionReferenceNo={paymentDraftFull.transactionReferenceNo}
            payOrderNo={paymentDraftFull.payOrderNo}
            chequeNo={paymentDraftFull.chequeNo}
            depositSlipNo={paymentDraftFull.depositSlipNo}
            banks={banks}
            required
            labelClassName="text-xs font-semibold text-foreground"
            inputClassName="glass-input h-10"
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 w-full min-w-0 items-end"
            onBankIdChange={(bankId) => setPaymentDraft((d) => ({ ...d, bankId }))}
            onTransactionReferenceChange={(transactionReferenceNo) =>
              setPaymentDraft((d) => ({ ...d, referenceNo: transactionReferenceNo }))
            }
            onPayOrderNoChange={(payOrderNo) => setPaymentDraft((d) => ({ ...d, referenceNo: payOrderNo }))}
            onChequeNoChange={(chequeNo) => setPaymentDraft((d) => ({ ...d, chequeNo }))}
            onDepositSlipNoChange={(depositSlipNo) => setPaymentDraft((d) => ({ ...d, referenceNo: depositSlipNo }))}
            disabled={!selectedEmployeeId}
          />
        ) : null}

        <div className="space-y-2">
          <Label className="text-xs font-semibold text-foreground">Reason / Note</Label>
          <Input
            className="glass-input h-10"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Emergency advance request"
            disabled={!selectedEmployeeId}
          />
        </div>

        <DialogFooter className="pt-4 border-t border-border/50 gap-2">
          <Button variant="outline" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void save()}
            disabled={saving || !selectedEmployeeId || metaLoading || exceedsCap || (periodSelected && availableCap <= 0.005)}
            className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Recording...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Record Advance
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
